import { FeatureFlagsServiceBase } from "@skylab-shared/config";
import { RootContextBase } from "@skylab-shared/core";
import { ModuleName } from "@skylab-shared/dataModel";
import { Singleton } from "@skylab-shared/utils";
import { CompanyFeaturesQuery } from "../company/queries/company-features.query-base";
import { LaboratoryInfoQuery } from "../laboratory/queries/laboratory-info.query-base";
import { LicenseKind, UserLicensesQuery } from "../license/queries/user-license.query-base";
import { availabilityCheckerFactory, AvailabilityService } from "./availability";
import { withFeature } from "./feature-availability";

export type Module = 'MailTemplateEditing' | 'DownloadOnlineExchangeFile' | 'Inventory' | 'Fiscalization' | 'Artemis' | 'KsefDocumentXmlExport' | 'KsefDocumentXmlImport';

@Singleton
class ModuleAvailabilityBase extends AvailabilityService<Module> {
	private modules: Record<Module, boolean>;

	protected async onRegisterAsync(
		rootContext: RootContextBase
	): Promise<void> {
		const ksefXmlLabActive = await this.getKsefXmlImportExportAvailability(rootContext);

		this.modules = {
			Inventory: await this.getInventoryLicenseAvailability(rootContext),
			KsefDocumentXmlExport: withFeature('ksefDocumentXmlExportFeature') && ksefXmlLabActive,
			KsefDocumentXmlImport: withFeature('ksefDocumentXmlImportFeature') && ksefXmlLabActive,
			MailTemplateEditing: await this.getMailTemplateEditingAvailability(rootContext),
			DownloadOnlineExchangeFile: await this.getDownloadOnlineExchangeFileAvailability(rootContext),
			Fiscalization: await this.getFiscalizationLicenseAvailability(rootContext),
			Artemis: withFeature('artemisModuleFeature') && await this.getOrderLabAvailability(rootContext)
		};
	}

	protected onCheck(modules: Module | Module[]): boolean {
		if (Array.isArray(modules)) {
			return modules.reduce((pV, cV) => pV && !!this.modules[cV], true);
		} else {
			return !!this.modules[modules];
		}
	};

	public async getFiscalizationLicenseAvailability(rootContext: RootContextBase): Promise<boolean> {
		const licenses = await rootContext.getAsync(UserLicensesQuery);
		const fiscalizationModule = licenses.modules?.find(license => license.name === ModuleName.Fiskalizacja);

		return !!fiscalizationModule?.active;
	}

	public async getInventoryLicenseAvailability(rootContext: RootContextBase): Promise<boolean> {
		const licenses = await rootContext.getAsync(UserLicensesQuery);
		const inventoryModule = licenses.modules?.find(license => license.name === ModuleName.Magazyn);

		return !!inventoryModule?.active;
	}

	public async getKsefDocumentXmlExportAvailability(featureFlagsService: FeatureFlagsServiceBase): Promise<boolean> {
		return featureFlagsService.flags.ksefDocumentXmlExportFeature;
	}

	public async getKsefDocumentXmlImportAvailability(featureFlagsService: FeatureFlagsServiceBase): Promise<boolean> {
		return featureFlagsService.flags.ksefDocumentXmlImportFeature;
	}

	public async getMailTemplateEditingAvailability(rootContext: RootContextBase): Promise<boolean> {
		const features = await rootContext.getAsync(CompanyFeaturesQuery);
		const license = await rootContext.getAsync(UserLicensesQuery);

		return (license.kind !== LicenseKind.Demonstracyjna_1M) || features.demoLicenseWithCustomEmailPattern;
	}

	public async getDownloadOnlineExchangeFileAvailability(rootContext: RootContextBase): Promise<boolean> {
		const features = await rootContext.getAsync(CompanyFeaturesQuery);
		return features.isAllowedToDownloadOnlineExchangeFile;
	}

	public async getOrderLabAvailability(rootContext: RootContextBase): Promise<boolean> {
		const laboratoryInfo = await rootContext.getAsync(LaboratoryInfoQuery);
		return laboratoryInfo?.some(elem => elem.context === 'SKYLAB_ORDERS');
	}

	public async getKsefXmlImportExportAvailability(rootContext: RootContextBase): Promise<boolean> {
		const laboratoryInfo = await rootContext.getAsync(LaboratoryInfoQuery);
		return laboratoryInfo?.some(elem => elem.context === 'SKYLAB_KSEF_XML');
	}
};

export const ModuleAvailability = new ModuleAvailabilityBase();
export const withModule = availabilityCheckerFactory(ModuleAvailability);
